/**
 * TimeEntryModal — Phase 3d create + edit modal for a single time entry.
 *
 * One component, two modes:
 *
 *   mode === 'create'      — clicked an empty calendar cell. Pre-fills
 *                            startedAt + endedAt; shows a Project +
 *                            Card picker (both optional). Submit calls
 *                            `useCreateTimeEntry`.
 *
 *   mode === 'edit'        — clicked an existing block. Pre-fills from
 *                            the entry. Project / card pickers are
 *                            editable: changing them re-links the entry
 *                            via PATCH (BE re-resolves projectId from
 *                            the new deliverable). Submit calls
 *                            `useUpdateTimeEntry`. Footer adds a Delete
 *                            button (pending days only) that opens the
 *                            shared `DeleteTimeEntryModal` confirm; a
 *                            successful delete closes both.
 *
 * Field set:
 *   - Project (Dropdown of `useProjects()`).
 *   - Card    (Dropdown of `useBoard(projectId).cards`, filtered to the
 *             chosen project; disabled until project is picked).
 *   - Start   (`<input type="datetime-local">`).
 *   - End     (`<input type="datetime-local">`).
 *   - Notes   (`<textarea>`).
 *
 * Validation: end > start (required); deliverableKind/Id are
 * both-or-neither (the BE enforces this too — we mirror it client-side
 * so the modal can keep the user inside it on input error).
 */

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { localizeError } from '@/api/error-codes'

import type { BoardCard, DeliverableKind, TimeEntry } from '@/api/types-bridge'
import { Button } from '@/components/Button'
import { Modal } from '@/components/Modal'
import { useToast } from '@/components/ToastProvider'
import { useBoard } from '@/features/board/use-board'
import { DeliverableSearchPicker } from '@/features/deliverables/DeliverableSearchPicker'
import type { SearchDeliverableResult } from '@/features/deliverables/use-search-deliverables'
import { useProjects } from '@/features/projects/use-projects'
import { useOrganization } from '@/features/tenant-settings/use-organization'
import {
  type Period,
  calculateDuration,
  isoToTimeParts,
  timePartsToIso,
  toIsoDate,
  validateTimeInput,
} from '@/features/time/date-helpers'
import { isDayLocked } from '@/features/time/lib/day-lock'
import { snapDate, snapWouldChange } from '@/features/time/lib/snap'
import {
  type CreateTimeEntryArgs,
  useCreateTimeEntry,
  useTimesheetDays,
  useUpdateTimeEntry,
} from '@/features/time/use-time-entries'
import { DeleteTimeEntryModal } from './DeleteTimeEntryModal'

/* -------------------------------------------------------------------------- */
/*                              datetime-local <-> ISO                        */
/* -------------------------------------------------------------------------- */

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function dateToLocalInput(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`
}

function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return dateToLocalInput(d)
}

function localInputToIso(local: string): string | null {
  if (!local) return null
  const d = new Date(local)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

function durationSecondsOf(startedAt: string | null, endedAt: string | null): number {
  if (!startedAt || !endedAt) return 0
  const start = new Date(startedAt).getTime()
  const end = new Date(endedAt).getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return 0
  return Math.max(0, Math.floor((end - start) / 1000))
}

/* -------------------------------------------------------------------------- */
/*                              Public API                                    */
/* -------------------------------------------------------------------------- */

interface CreateModeProps {
  open: boolean
  onClose: () => void
  mode: 'create'
  /** Pre-filled start. Caller is responsible for snapping to 15min. */
  initialStart: Date
  /** Pre-filled end. Defaults caller-side to start + 30min. */
  initialEnd: Date
  /** Pre-fills the project picker if the caller has one in mind. */
  initialProjectId?: string
  /** Optional initial card link (kind required if id is present). */
  initialDeliverableKind?: DeliverableKind
  initialDeliverableId?: string
  /**
   * Wave 2 Batch G — when true (caller opened the modal from a
   * timer-start surface), the cross-project deliverable picker only
   * shows cards in columns where `allowsTimer = true`. Default false:
   * manual entries are allowed against any card the user can see.
   */
  timerMode?: boolean
}

interface EditModeProps {
  open: boolean
  onClose: () => void
  mode: 'edit'
  entry: TimeEntry
  /** See `CreateModeProps.timerMode`. */
  timerMode?: boolean
}

/**
 * Read-only view variant — used when the user clicks the "eye" icon on
 * an entry whose timesheet day has been submitted/approved. Same field
 * layout as edit mode but every input is disabled and the footer is
 * replaced with a single Close button. A muted banner at the top
 * surfaces the lock state.
 */
interface ViewModeProps {
  open: boolean
  onClose: () => void
  mode: 'view'
  entry: TimeEntry
}

export type TimeEntryModalProps = CreateModeProps | EditModeProps | ViewModeProps

export function TimeEntryModal(props: TimeEntryModalProps) {
  const { t } = useTranslation('time')
  // Granular key — any meaningful change to "what's being edited" forces
  // a fresh mount of the body (lazy useState re-runs, draft state cleared).
  // Within a single create/edit session, the key is stable so the parent
  // can re-render at any rate (e.g. WeekGrid's 1s now-tick) without
  // wiping the user's typed input. R-2 caught the unstable-key bug.
  const bodyKey =
    props.mode === 'edit'
      ? `edit-${props.entry.id}`
      : props.mode === 'view'
        ? `view-${props.entry.id}`
        : `create-${props.initialStart.getTime()}-${props.initialEnd.getTime()}-${props.initialDeliverableId ?? ''}`
  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      size="xl"
      ariaLabel={
        props.mode === 'create'
          ? t('entryModal.ariaCreate')
          : props.mode === 'view'
            ? t('entryModal.ariaView')
            : t('entryModal.ariaEdit')
      }
    >
      {props.open && <TimeEntryModalBody key={bodyKey} {...props} />}
    </Modal>
  )
}

/* -------------------------------------------------------------------------- */
/*                              Body                                          */
/* -------------------------------------------------------------------------- */

function TimeEntryModalBody(props: TimeEntryModalProps) {
  const { t } = useTranslation('time')
  const { t: tCommon } = useTranslation('common')
  const toast = useToast()
  const projectsQuery = useProjects()
  const projects = projectsQuery.data ?? []

  // Wave 2 Batch G — single combobox replaces the prior Project + Card
  // dropdowns. The picker carries enough metadata (kind/id/projectId/
  // shorthand/title/projectName) to drive every form field that used to
  // depend on the per-project board fetch.
  //
  // For edit-mode pre-fill we need to seed the picker with a
  // SearchDeliverableResult-shaped object derived from the existing
  // entry. The entry already carries `shorthand` + `deliverableTitle` (BE
  // joins those in Phase 3c-2), and `projectId` + `deliverableKind`. We
  // synthesize a stub for the pill display; if the user reopens the
  // search, real results from `/api/deliverables/search` flow through.

  const [selectedCard, setSelectedCard] = useState<SearchDeliverableResult | null>(() =>
    seedSelectedCard(props, projects),
  )
  // "Select Type" radio — narrows the deliverable search picker by kind.
  // Edit-mode opens with the entry's existing kind selected; create-mode
  // defaults to `task` (the most common manual-log surface).
  const [kindFilter, setKindFilter] = useState<'task' | 'ticket' | 'event'>(() => {
    if ((props.mode === 'edit' || props.mode === 'view') && props.entry.deliverableKind) {
      const k = props.entry.deliverableKind
      if (k === 'task' || k === 'ticket' || k === 'event') return k
    }
    return 'task'
  })
  // Legacy-shape duration state: 6 parts driving a compound input
  // (time + AM/PM + date) per row. Source of truth in this form; we
  // compose to ISO at submit time.
  const initialParts = useMemo(() => readInitialParts(props), [props])
  const [startTime, setStartTime] = useState<string>(initialParts.startTime)
  const [startPeriod, setStartPeriod] = useState<Period>(initialParts.startPeriod)
  const [startDate, setStartDate] = useState<string>(initialParts.startDate)
  const [endTime, setEndTime] = useState<string>(initialParts.endTime)
  const [endPeriod, setEndPeriod] = useState<Period>(initialParts.endPeriod)
  const [endDate, setEndDate] = useState<string>(initialParts.endDate)
  const [notes, setNotes] = useState<string>(() => readInitial(props).notes)
  const [error, setError] = useState<string | null>(null)

  // Re-seed the pill once /api/projects resolves and we can fill in
  // projectName / projectCode for the existing entry. Only fires on edit-
  // mode mounts where the entry has a deliverable.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberate one-shot effect — we want it to run when projects.length transitions from 0 to N (initial fetch landed), not on every change to props/projects identity. setSelectedCard's identity is stable per React's contract.
  useEffect(() => {
    if (selectedCard !== null) return
    const seed = seedSelectedCard(props, projects)
    if (seed) setSelectedCard(seed)
  }, [projects.length])

  // Best-effort enrichment: if the picker was seeded as a stub (no
  // shorthand, projectName, or statusName), fetch the project's board
  // and patch in the real card data once. The board fetch is cheap
  // (15s stale time) and lets the pill show
  // "MOTE-3 · Title · Project Name · [Today]" instead of just "Title".
  const needsEnrichment =
    selectedCard != null &&
    (!selectedCard.shorthand || !selectedCard.projectName || !selectedCard.statusName)
  const seededProjectId = needsEnrichment ? selectedCard.projectId : null
  // `includeHidden` — enrichment is id→card RESOLUTION, not board
  // rendering. Time gets logged against work that then finishes, so a
  // linked card ageing out of the Done column is the normal end state;
  // filtered, editing that entry would show a bare title with no
  // shorthand, project, or status pill.
  const board = useBoard(seededProjectId || undefined, { includeHidden: true })
  const cards: BoardCard[] = useMemo(() => board.data?.cards ?? [], [board.data])
  const statuses = useMemo(() => board.data?.statuses ?? [], [board.data])
  useEffect(() => {
    if (!selectedCard) return
    if (selectedCard.shorthand && selectedCard.projectName && selectedCard.statusName) return
    const card = cards.find((c) => c.id === selectedCard.id)
    if (!card) return
    const proj = projects.find((p) => p.id === selectedCard.projectId)
    const status = statuses.find((s) => s.id === card.statusId)
    setSelectedCard((prev) =>
      prev
        ? {
            ...prev,
            shorthand: card.shorthand ?? prev.shorthand,
            title: card.title || prev.title,
            projectCode: proj?.code ?? prev.projectCode,
            projectName: proj?.name ?? prev.projectName,
            statusName: status?.name ?? prev.statusName,
            allowsTimer: status?.allowsTimer ?? prev.allowsTimer,
          }
        : prev,
    )
  }, [cards, statuses, projects, selectedCard])

  const createMutation = useCreateTimeEntry()
  const updateMutation = useUpdateTimeEntry()

  // Lock check: query the picked start date's timesheet status. If it's
  // already `submitted` / `approved`, block submission and surface a
  // warning in the modal. Only runs in create mode (edit's lock is BE-
  // enforced + the user can't edit a locked entry from the calendar
  // anyway). Single-day window — cheap.
  const lockCheck = useTimesheetDays(
    { start: startDate || toIsoDate(new Date()), end: startDate || toIsoDate(new Date()) },
    { enabled: props.mode === 'create' && Boolean(startDate) },
  )
  const pickedDay = lockCheck.data?.days[0]
  const pickedDayStatus = pickedDay?.status ?? 'pending'
  // A synthesized past empty day is a render hint, not a lock — the user
  // can still create entries there (see `lib/day-lock.ts`).
  const isPickedDayLocked = props.mode === 'create' && isDayLocked(pickedDay)

  const isPending = createMutation.isPending || updateMutation.isPending
  // View-mode reuses the edit-mode layout but freezes every input and
  // strips the Save/Delete footer. The user gets a read-only snapshot of
  // the locked entry; mutations are unreachable.
  const readOnly = props.mode === 'view'
  // Delete lives in edit mode only, and only while the day is still open —
  // the BE refuses a delete on a submitted or approved day.
  const canDelete = props.mode === 'edit' && props.entry.timesheetStatus === 'pending'
  const [confirmDelete, setConfirmDelete] = useState(false)
  const inputsDisabled = isPending || readOnly

  const duration = useMemo(
    () => calculateDuration(startTime, startPeriod, endTime, endPeriod, startDate, endDate),
    [startTime, startPeriod, endTime, endPeriod, startDate, endDate],
  )

  // No future entries — 60s skew tolerance matches the BE. Drives both
  // the inline warning banner below the Duration row and the Submit
  // button's disabled state. We recompute when the parts change so the
  // user gets immediate feedback as they tweak the inputs.
  const isFutureEntry = useMemo(() => {
    const startIso = timePartsToIso(startTime, startPeriod, startDate)
    const endIso = timePartsToIso(endTime, endPeriod, endDate)
    if (!startIso || !endIso) return false
    const cutoff = Date.now() + 60_000
    return new Date(startIso).getTime() > cutoff || new Date(endIso).getTime() > cutoff
  }, [startTime, startPeriod, startDate, endTime, endPeriod, endDate])

  // Live preview of how the BE's rounding config would transform the
  // entered start / end values. Returns null when (a) the org has the
  // side turned off, (b) parsing fails, or (c) the entered value
  // already lands on the grid — so the hint stays hidden in the
  // common "no-snap-needed" case and only surfaces when there's a
  // concrete consequence. BE is the canonical guard; this is purely
  // a UX safety net so users aren't surprised post-submit.
  const org = useOrganization()
  const startSnapPreview = useMemo(() => {
    const cfg = org.data
    if (!cfg) return null
    const iso = timePartsToIso(startTime, startPeriod, startDate)
    if (!iso) return null
    const d = new Date(iso)
    if (!snapWouldChange(d, cfg.timeRoundStartMinutes, cfg.timeRoundStartDirection)) return null
    const snapped = snapDate(d, cfg.timeRoundStartMinutes, cfg.timeRoundStartDirection)
    const parts = isoToTimeParts(snapped.toISOString())
    return t('entryModal.snapPreview', { value: `${parts.time} ${parts.period}` })
  }, [org.data, startTime, startPeriod, startDate, t])
  const endSnapPreview = useMemo(() => {
    const cfg = org.data
    if (!cfg) return null
    const iso = timePartsToIso(endTime, endPeriod, endDate)
    if (!iso) return null
    const d = new Date(iso)
    if (!snapWouldChange(d, cfg.timeRoundStopMinutes, cfg.timeRoundStopDirection)) return null
    const snapped = snapDate(d, cfg.timeRoundStopMinutes, cfg.timeRoundStopDirection)
    const parts = isoToTimeParts(snapped.toISOString())
    return t('entryModal.snapPreview', { value: `${parts.time} ${parts.period}` })
  }, [org.data, endTime, endPeriod, endDate, t])

  const handleSubmit = async () => {
    setError(null)
    const startIso = timePartsToIso(startTime, startPeriod, startDate)
    const endIso = timePartsToIso(endTime, endPeriod, endDate)
    if (!startIso) {
      setError(t('entryModal.errorStartRequired'))
      return
    }
    if (!endIso) {
      setError(t('entryModal.errorEndRequired'))
      return
    }
    if (new Date(endIso).getTime() <= new Date(startIso).getTime()) {
      setError(t('entryModal.errorEndAfterStart'))
      return
    }
    const futureCutoff = Date.now() + 60_000
    if (new Date(startIso).getTime() > futureCutoff || new Date(endIso).getTime() > futureCutoff) {
      setError(t('entryModal.errorFuture'))
      return
    }
    if (isPickedDayLocked) {
      setError(
        t('entryModal.errorDayLocked', {
          date: startDate,
          status: pickedDayStatus,
        }),
      )
      return
    }

    if (props.mode === 'create') {
      const args: CreateTimeEntryArgs = {
        startedAt: startIso,
        endedAt: endIso,
        notes: notes.trim() || undefined,
      }
      if (selectedCard) {
        args.projectId = selectedCard.projectId
        args.deliverableKind = selectedCard.kind
        args.deliverableId = selectedCard.id
      }
      try {
        await createMutation.mutateAsync(args)
        toast.success(t('entryModal.createdToast'))
        props.onClose()
      } catch (err) {
        // Localize via the wire `code` rather than rendering the BE's
        // English `message` — that dev fallback is what surfaced for a
        // guest's `guest_forbidden` refusal instead of the catalog copy.
        setError(err instanceof Error ? localizeError(err, t) : t('entryModal.errorCreate'))
      }
      return
    }

    // Edit mode — only patch fields that changed.
    const entry = props.entry
    const patch: {
      startedAt?: string
      endedAt?: string | null
      notes?: string
      deliverableKind?: DeliverableKind | null
      deliverableId?: string | null
      projectId?: string | null
    } = {}
    if (startIso !== entry.startedAt) patch.startedAt = startIso
    if (endIso !== entry.endedAt) patch.endedAt = endIso
    if (notes !== (entry.notes ?? '')) patch.notes = notes

    // Re-link the entry if the user picked a different card (or cleared).
    const nextKind: DeliverableKind | null = selectedCard?.kind ?? null
    const nextDeliverableId: string | null = selectedCard?.id ?? null
    const nextProjectId: string | null = selectedCard?.projectId ?? null
    const linkChanged =
      nextKind !== entry.deliverableKind ||
      nextDeliverableId !== entry.deliverableId ||
      nextProjectId !== entry.projectId
    if (linkChanged) {
      if (nextKind && nextDeliverableId) {
        patch.deliverableKind = nextKind
        patch.deliverableId = nextDeliverableId
        patch.projectId = nextProjectId
      } else {
        patch.deliverableKind = null
        patch.deliverableId = null
        patch.projectId = null
      }
    }

    if (Object.keys(patch).length === 0) {
      props.onClose()
      return
    }
    try {
      await updateMutation.mutateAsync({ id: entry.id, patch })
      toast.success(t('entryModal.updatedToast'))
      props.onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('entryModal.errorSave'))
    }
  }

  const inputBase =
    'block w-full rounded-md border border-border-strong bg-bg-elevated px-3 py-2 text-sm text-fg shadow-sm focus:border-fg focus:outline-none focus:ring-1 focus:ring-fg disabled:cursor-not-allowed disabled:bg-bg-muted'

  const headerTitle =
    props.mode === 'create'
      ? t('entryModal.createTitle')
      : props.mode === 'view'
        ? t('entryModal.viewTitle')
        : t('entryModal.editTitle')
  const submitLabel =
    props.mode === 'create'
      ? createMutation.isPending
        ? t('entryModal.creating')
        : t('entryModal.createSubmit')
      : updateMutation.isPending
        ? t('entryModal.saving')
        : t('entryModal.saveSubmit')

  return (
    <>
      <Modal.Header>
        <div className="flex items-baseline justify-between">
          <span>{headerTitle}</span>
        </div>
      </Modal.Header>
      <Modal.Body className="space-y-4">
        {/* Select Type — narrows the search to one deliverable kind (legacy parity). */}
        <div className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-fg">{t('entryModal.selectTypeHeading')}</span>
          <div
            role="radiogroup"
            aria-label={t('entryModal.selectTypeAria')}
            className="flex flex-wrap items-center gap-5"
          >
            {(['event', 'task', 'ticket'] as const).map((k) => (
              <label
                key={k}
                className={`inline-flex cursor-pointer items-center gap-2 text-sm ${
                  isPending ? 'opacity-60' : ''
                }`}
              >
                <input
                  type="radio"
                  name="entry-kind-filter"
                  value={k}
                  checked={kindFilter === k}
                  onChange={() => {
                    if (kindFilter === k) return
                    setKindFilter(k)
                    // Clear the selection when changing kind so the picker
                    // doesn't show a stale pill from the previous filter.
                    if (selectedCard && selectedCard.kind !== k) setSelectedCard(null)
                  }}
                  disabled={inputsDisabled}
                  className="h-4 w-4 accent-accent"
                />
                <span className="font-medium text-fg">
                  {k === 'event'
                    ? t('entryModal.kindEvent')
                    : k === 'task'
                      ? t('entryModal.kindTask')
                      : t('entryModal.kindTicket')}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Title (deliverable search picker) — legacy pill-label layout. */}
        <FieldRow icon={<PenIcon />} label={t('entryModal.titleLabel')}>
          <DeliverableSearchPicker
            value={selectedCard}
            onChange={(next) => setSelectedCard(next)}
            kind={kindFilter}
            allowsTimerOnly={props.mode !== 'view' ? props.timerMode : undefined}
            disabled={inputsDisabled}
            placeholder={t('entryModal.cardPlaceholder')}
          />
        </FieldRow>

        {/* Notes — sits between Title and Duration per legacy spec. */}
        <FieldRow icon={<NotesIcon />} label={t('entryModal.notesPillLabel')} alignTop>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={inputsDisabled}
            rows={3}
            className={`${inputBase} resize-y`}
            placeholder={t('entryModal.notesPlaceholder')}
          />
        </FieldRow>

        {/* Duration selector — legacy parity. Two compound rows (Start /
            End), each with a time input + AM/PM dropdown + date pill, and
            a live duration counter on the right. */}
        <div className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-fg">{t('entryModal.durationHeading')}</span>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
            <div className="flex flex-1 flex-col gap-2">
              <div className="flex flex-col gap-0.5">
                <DurationRow
                  label={t('entryModal.startLabel')}
                  kind="start"
                  time={startTime}
                  period={startPeriod}
                  date={startDate}
                  onTimeChange={setStartTime}
                  onPeriodChange={setStartPeriod}
                  onDateChange={(value) => {
                    setStartDate(value)
                    // Auto-sync end date to match start (legacy parity).
                    if (value > endDate) setEndDate(value)
                  }}
                  disabled={inputsDisabled}
                />
                {/* Snap preview — kept deliberately tight (10px,
                    leading-none, gap-0.5 above) so its presence
                    doesn't shove the row column past the duration
                    block on the right and skew the alignment. Hidden
                    when raw==snapped so the common no-snap case shows
                    nothing at all. */}
                {startSnapPreview && (
                  <p className="ms-[100px] text-[10px] leading-none text-fg-muted">
                    {startSnapPreview}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-0.5">
                <DurationRow
                  label={t('entryModal.endLabel')}
                  kind="end"
                  time={endTime}
                  period={endPeriod}
                  date={endDate}
                  minDate={startDate || undefined}
                  onTimeChange={setEndTime}
                  onPeriodChange={setEndPeriod}
                  onDateChange={setEndDate}
                  disabled={inputsDisabled}
                />
                {endSnapPreview && (
                  <p className="ms-[100px] text-[10px] leading-none text-fg-muted">
                    {endSnapPreview}
                  </p>
                )}
              </div>
            </div>
            <div
              className={`flex w-full items-center justify-center rounded-md border px-3 py-3 sm:w-32 ${
                duration.isEndBeforeStart || duration.isMoreThanEightHours
                  ? 'border-danger/40 bg-danger/10 text-danger-ink'
                  : 'border-border-strong bg-bg-muted text-fg'
              }`}
            >
              <span className="whitespace-nowrap text-lg font-bold tabular-nums">
                {duration.duration}
              </span>
            </div>
          </div>
        </div>

        {/* Read-only banner — surfaced only in view mode. Communicates
            why the form is frozen and points the user at the manager-
            reject recovery path. */}
        {readOnly && (
          <output className="block rounded-md border border-border bg-bg-muted px-3 py-2 text-xs text-fg-muted">
            {t('entryModal.viewBanner')}
          </output>
        )}

        {/* Inline warning when the picked start date is on a locked
            (submitted/approved) timesheet day. The BE also rejects with
            400, but blocking on the FE saves a round-trip and gives the
            user immediate feedback before they hit Submit. */}
        {isPickedDayLocked && (
          <p
            role="alert"
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-900/30 dark:text-amber-100"
          >
            {t('entryModal.dayLockedWarning', {
              date: startDate,
              status: pickedDayStatus,
            })}
          </p>
        )}

        {isFutureEntry && !readOnly && (
          <p
            role="alert"
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-900/30 dark:text-amber-100"
          >
            {t('entryModal.futureWarning')}
          </p>
        )}

        {error && (
          <p role="alert" className="text-xs text-danger">
            {error}
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        {readOnly ? (
          <Button variant="secondary" onClick={props.onClose}>
            {tCommon('actions.close')}
          </Button>
        ) : (
          <>
            {canDelete && (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                disabled={isPending}
                className="me-auto inline-flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-danger-ink transition-colors hover:bg-danger/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-60"
              >
                <TrashIcon />
                {t('entryModal.delete')}
              </button>
            )}
            <Button variant="secondary" onClick={props.onClose} disabled={isPending}>
              {tCommon('actions.cancel')}
            </Button>
            <Button
              onClick={() => void handleSubmit()}
              disabled={isPending || isPickedDayLocked || isFutureEntry}
            >
              {submitLabel}
            </Button>
          </>
        )}
      </Modal.Footer>
      {props.mode === 'edit' && confirmDelete && (
        <DeleteTimeEntryModal
          open
          entry={props.entry}
          onClose={() => setConfirmDelete(false)}
          onDeleted={props.onClose}
          stacked
        />
      )}
    </>
  )
}

/* -------------------------------------------------------------------------- */
/*                              Duration row                                  */
/* -------------------------------------------------------------------------- */

interface DurationRowProps {
  label: string
  kind: 'start' | 'end'
  time: string
  period: Period
  date: string
  minDate?: string
  onTimeChange: (value: string) => void
  onPeriodChange: (value: Period) => void
  onDateChange: (value: string) => void
  disabled?: boolean
}

function DurationRow({
  label,
  kind,
  time,
  period,
  date,
  minDate,
  onTimeChange,
  onPeriodChange,
  onDateChange,
  disabled,
}: DurationRowProps) {
  return (
    <div className="flex flex-wrap items-stretch gap-2">
      <div className="inline-flex h-10 w-[92px] shrink-0 items-center gap-1.5 rounded-md bg-bg-muted px-3 text-xs font-semibold text-fg-muted">
        <ClockIcon kind={kind} />
        <span>{label}</span>
      </div>
      <TimeInput
        value={time}
        period={period}
        onTimeChange={onTimeChange}
        onPeriodChange={onPeriodChange}
        disabled={disabled}
      />
      <DateField value={date} minDate={minDate} onChange={onDateChange} disabled={disabled} />
    </div>
  )
}

interface TimeInputProps {
  value: string
  period: Period
  onTimeChange: (value: string) => void
  onPeriodChange: (value: Period) => void
  disabled?: boolean
}

function TimeInput({ value, period, onTimeChange, onPeriodChange, disabled }: TimeInputProps) {
  const { t } = useTranslation('time')
  return (
    <div
      className={`inline-flex h-10 w-[140px] items-center gap-2 rounded-md border border-border-strong bg-bg-elevated px-3 ${
        disabled ? 'opacity-60' : ''
      }`}
    >
      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => onTimeChange(validateTimeInput(e.target.value))}
        placeholder="0:00"
        disabled={disabled}
        aria-label={t('entryModal.timeAria')}
        className="w-full bg-transparent text-sm tabular-nums text-fg placeholder:text-fg-subtle focus:outline-none"
      />
      <select
        value={period}
        onChange={(e) => onPeriodChange(e.target.value as Period)}
        disabled={disabled}
        aria-label="AM or PM"
        className="bg-transparent text-sm font-semibold text-fg focus:outline-none"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  )
}

interface DateFieldProps {
  value: string
  minDate?: string
  onChange: (value: string) => void
  disabled?: boolean
}

function DateField({ value, minDate, onChange, disabled }: DateFieldProps) {
  const { t, i18n } = useTranslation('time')
  const inputRef = useRef<HTMLInputElement | null>(null)
  const displayValue = (() => {
    if (!value) return t('entryModal.selectDate')
    const todayIso = toIsoDate(new Date())
    if (value === todayIso) return t('entryModal.today')
    const d = new Date(`${value}T12:00:00`)
    if (Number.isNaN(d.getTime())) return value
    // Short month so the rendered width is consistent across May / Sep /
    // Dec, etc. The button itself is fixed-width below; this just keeps
    // long months (September etc.) from looking truncated.
    return d.toLocaleDateString(i18n.language, { day: '2-digit', month: 'short', year: 'numeric' })
  })()

  const handleOpen = () => {
    const inp = inputRef.current
    if (!inp) return
    // showPicker() is the modern API; falls back to focus+click for older browsers.
    if (typeof inp.showPicker === 'function') {
      try {
        inp.showPicker()
        return
      } catch {
        // Some browsers throw when the input is hidden; fall through to click.
      }
    }
    inp.focus()
    inp.click()
  }

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className={`inline-flex h-10 w-[140px] items-center justify-between gap-2 rounded-md border border-border-strong bg-bg-elevated px-3 text-sm text-fg shadow-sm transition-colors hover:bg-bg-muted focus:outline-none focus:ring-2 focus:ring-fg focus:ring-offset-1 ${
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        }`}
      >
        <span>{displayValue}</span>
        <CalendarIcon />
      </button>
      <input
        ref={inputRef}
        type="date"
        value={value}
        min={minDate}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        // sr-only-ish: kept in the DOM at zero size so showPicker()/click() works.
        className="pointer-events-none absolute inset-0 h-0 w-0 opacity-0"
        aria-hidden="true"
        tabIndex={-1}
      />
    </div>
  )
}

function ClockIcon({ kind }: { kind: 'start' | 'end' }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <title>{kind === 'start' ? 'Start' : 'End'}</title>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}

/**
 * Pill-labeled field row used for Title and Notes. The pill sits to the
 * left of the input, matching the legacy modal layout.
 */
function FieldRow({
  icon,
  label,
  alignTop,
  children,
}: {
  icon: ReactNode
  label: string
  alignTop?: boolean
  children: ReactNode
}) {
  return (
    <div className={`flex flex-wrap gap-2 ${alignTop ? 'items-start' : 'items-center'}`}>
      <div
        className={`inline-flex h-10 w-[92px] shrink-0 items-center gap-1.5 rounded-md bg-bg-muted px-3 text-xs font-semibold text-fg-muted ${
          alignTop ? 'self-start' : ''
        }`}
      >
        {icon}
        <span>{label}</span>
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

function PenIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  )
}

function NotesIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <line x1="8" y1="9" x2="16" y2="9" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="12" y2="17" />
    </svg>
  )
}

function CalendarIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}

/* -------------------------------------------------------------------------- */
/*                              Initial values                                */
/* -------------------------------------------------------------------------- */

interface InitialState {
  projectId: string
  deliverableId: string
  deliverableKind: DeliverableKind | ''
  startedAt: string
  endedAt: string
  notes: string
}

function readInitial(props: TimeEntryModalProps): InitialState {
  if (props.mode === 'create') {
    return {
      projectId: props.initialProjectId ?? '',
      deliverableId: props.initialDeliverableId ?? '',
      deliverableKind: props.initialDeliverableKind ?? '',
      startedAt: dateToLocalInput(props.initialStart),
      endedAt: dateToLocalInput(props.initialEnd),
      notes: '',
    }
  }
  // edit + view share the entry-seeded shape.
  const e = props.entry
  return {
    projectId: e.projectId ?? '',
    deliverableId: e.deliverableId ?? '',
    deliverableKind: (e.deliverableKind ?? '') as DeliverableKind | '',
    startedAt: isoToLocalInput(e.startedAt),
    endedAt: isoToLocalInput(e.endedAt),
    notes: e.notes ?? '',
  }
}

interface InitialParts {
  startTime: string
  startPeriod: Period
  startDate: string
  endTime: string
  endPeriod: Period
  endDate: string
}

/** Initial parts for the compound duration selector. */
function readInitialParts(props: TimeEntryModalProps): InitialParts {
  if (props.mode === 'create') {
    const s = isoToTimeParts(props.initialStart.toISOString())
    const e = isoToTimeParts(props.initialEnd.toISOString())
    return {
      startTime: s.time,
      startPeriod: s.period,
      startDate: s.date,
      endTime: e.time,
      endPeriod: e.period,
      endDate: e.date,
    }
  }
  // edit + view share the entry-seeded shape.
  const entry = props.entry
  const s = isoToTimeParts(entry.startedAt)
  const e = entry.endedAt
    ? isoToTimeParts(entry.endedAt)
    : { time: '', period: 'AM' as Period, date: s.date }
  return {
    startTime: s.time,
    startPeriod: s.period,
    startDate: s.date,
    endTime: e.time,
    endPeriod: e.period,
    endDate: e.date,
  }
}

/**
 * Seed the picker's selection from props. In edit mode this maps the
 * existing entry → SearchDeliverableResult-shaped stub for the pill;
 * fields we don't have yet (statusName, allowsTimer, projectCode,
 * projectName) are filled in later by the in-modal best-effort enrichment
 * effect once the projects list and the board fetch land.
 */
function seedSelectedCard(
  props: TimeEntryModalProps,
  projects: Array<{ id: string; name: string; code?: string }>,
): SearchDeliverableResult | null {
  if (props.mode === 'edit' || props.mode === 'view') {
    const e = props.entry
    if (!e.deliverableKind || !e.deliverableId || !e.projectId) return null
    const proj = projects.find((p) => p.id === e.projectId)
    return {
      kind: e.deliverableKind,
      id: e.deliverableId,
      title: e.deliverableTitle ?? '(card)',
      shorthand: e.shorthand ?? null,
      projectId: e.projectId,
      projectCode: proj?.code ?? '',
      projectName: proj?.name ?? '',
      statusName: '',
      allowsTimer: false,
    }
  }
  // Create-mode initial deliverable is rare (callers usually let the user
  // pick from inside the modal). When provided, render a minimal pill —
  // the search popover replaces it on next interaction.
  if (props.initialDeliverableKind && props.initialDeliverableId && props.initialProjectId) {
    const proj = projects.find((p) => p.id === props.initialProjectId)
    return {
      kind: props.initialDeliverableKind,
      id: props.initialDeliverableId,
      title: '(card)',
      shorthand: null,
      projectId: props.initialProjectId,
      projectCode: proj?.code ?? '',
      projectName: proj?.name ?? '',
      statusName: '',
      allowsTimer: false,
    }
  }
  return null
}

// Reference suppression: BoardCard import is type-only via DeliverableKind.
export type { BoardCard }

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  )
}
